import React from 'react';
import type { JSX } from 'react';
import { Crosshair, Radio } from 'lucide-react';
import type { LivePunkt } from '../../services/verortung/liveStandort';
import type { LiveStandort } from './useLiveStandort';

/**
 * Anzeige des Live-Standorts auf einem Plan (Betreiber 27.09.2026): blinkender
 * Punkt mit Richtungskegel und Unsicherheitskreis. Liegt als Schicht über dem
 * Planbild (Eltern-Element `relative`, gleiche Größe wie das Bild) und fängt
 * keine Tipps ab — getippt wird wie bisher auf den Plan („korrigieren“).
 */

export function LiveStandortMarker({ punkt }: { punkt: LivePunkt }): JSX.Element {
    const { pin, radius, blickGradPlan } = punkt;
    return (
        <div className="absolute inset-0 pointer-events-none" aria-label="Live-Standort">
            <div
                className="absolute rounded-full bg-sky-400/15 border border-sky-400/70"
                style={{ left: `${pin.x - radius.rx}%`, top: `${pin.y - radius.ry}%`, width: `${radius.rx * 2}%`, height: `${radius.ry * 2}%` }}
            />
            {blickGradPlan !== null && (
                <svg
                    className="absolute"
                    style={{ left: `${pin.x}%`, top: `${pin.y}%`, width: 96, height: 96, marginLeft: -48, marginTop: -48, transform: `rotate(${blickGradPlan}deg)` }}
                    viewBox="0 0 96 96"
                    aria-hidden="true"
                >
                    <defs>
                        <linearGradient id="live-kegel" x1="0" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#0284c7" stopOpacity="0.55" />
                            <stop offset="100%" stopColor="#0284c7" stopOpacity="0" />
                        </linearGradient>
                    </defs>
                    {/* Kegel ±30° nach „oben“ = Blickrichtung (gedreht). */}
                    <path d="M48 48 L24 6 A48 48 0 0 1 72 6 Z" fill="url(#live-kegel)" />
                </svg>
            )}
            <span className="absolute flex items-center justify-center" style={{ left: `${pin.x}%`, top: `${pin.y}%`, width: 0, height: 0 }}>
                <span className="absolute h-8 w-8 rounded-full bg-sky-400 opacity-60 animate-ping" />
                <span className="absolute h-4 w-4 rounded-full bg-sky-600 border-2 border-white shadow" />
            </span>
        </div>
    );
}

/**
 * Leiste über der Planansicht: was die Sensoren gerade sagen, Knopf „Live-Standort
 * übernehmen“ (setzt den Pin auf den Punkt) und „Live aus/an“ (Gerät, sofort).
 */
export function LiveStandortLeiste({
    live,
    punktImPlan,
    onUebernehmen,
    onZumLivePlan,
    dunkel = true,
}: {
    live: LiveStandort;
    /** Punkt auf dem gerade gezeigten Plan (für „übernehmen“). */
    punktImPlan: LivePunkt | null;
    onUebernehmen?: () => void;
    /** Gezeigter Plan ≠ Plan der geschätzten Ebene → Knopf „zum Plan …“. */
    onZumLivePlan?: () => void;
    dunkel?: boolean;
}): JSX.Element | null {
    if (!live.abschaltbar) return null;
    const s = live.schaetzung;
    const ebeneText = !live.aktiv
        ? 'Live aus'
        : !live.schnappschuss
            ? 'Live wird gestartet …'
            : s?.zwischen
                ? `zwischen ${s.zwischen[0].name} und ${s.zwischen[1].name}`
                : s?.ebene
                    ? `vermutlich ${s.ebene.name}`
                    : 'Geschoss unbekannt — Plan des Geschosses antippen';
    const gps = live.schnappschuss?.position;
    const gpsText = !live.aktiv ? '' : gps ? `GPS ±${Math.round(gps.genauigkeitM)} m` : 'kein GPS';
    const farbe = dunkel ? 'bg-slate-800 text-white border-slate-700' : 'bg-sky-50 text-slate-800 border-sky-200';
    return (
        <div className={`flex flex-wrap items-center gap-2 px-3 py-2 border-b text-sm ${farbe}`}>
            <span className="flex items-center gap-2 min-w-0">
                <Radio className={`w-4 h-4 shrink-0 ${live.aktiv ? 'text-sky-400 animate-pulse' : 'text-slate-400'}`} />
                <span className="font-semibold truncate">{ebeneText}</span>
                {gpsText && <span className="text-xs opacity-75">{gpsText}</span>}
                {live.aktiv && live.schnappschuss && !punktImPlan && (
                    <span className="text-xs opacity-75">· kein Live-Punkt auf diesem Plan</span>
                )}
            </span>
            <span className="ml-auto flex items-center gap-2">
                {live.aktiv && onZumLivePlan && (
                    <button type="button" onClick={onZumLivePlan} className="relative treffer-44 rounded-lg border border-sky-400 px-2.5 py-1.5 text-xs font-semibold text-sky-300 active:bg-sky-900/40">
                        Zum Plan des Geschosses
                    </button>
                )}
                {live.aktiv && punktImPlan && onUebernehmen && (
                    <button type="button" onClick={onUebernehmen} className="relative treffer-44 rounded-lg bg-sky-600 px-2.5 py-1.5 text-xs font-semibold text-white active:bg-sky-700 flex items-center gap-1">
                        <Crosshair className="w-3.5 h-3.5" /> Live-Standort übernehmen
                    </button>
                )}
                <button
                    type="button"
                    onClick={live.einblendungUmschalten}
                    className={`relative treffer-44 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${dunkel ? 'border-slate-500 text-slate-200' : 'border-slate-300 text-slate-700'}`}
                >
                    {live.einblendungAn ? 'Live aus' : 'Live an'}
                </button>
            </span>
        </div>
    );
}
